import { Injectable } from '@angular/core';
import { PageVisibility } from '../../models/page.interface';
import { ISectionPresenter } from '../../models/section-presenter';
import { PowerSmartSettingsSection } from './power-smart-settings-section/power-smart-settings-section';
import { BatterySettingsSection } from './battery-settings-section/battery-settings-section';
import { SmartStandbySection } from './smart-standby-section/smart-standby-section';
import { PowerSettingsSection } from './power-settings-section/power-settings-section';
import { EnergyStarSection } from './energy-star-section/energy-star-section';
import { DPMPowerSettingsSection } from './dpm-power-settings-section/dpm-power-settings-section';
import { PowerContainer } from './power-container/power-container';
import { Observable } from 'rxjs';
import { LoggerService } from 'src/app/services/logger/logger.service';

const POWER_SMART_SETTINGS_SECTION = 'powerSmartSettingsSection';
const BATTERY_SETTINGS_SECTION = 'batterySettingsSection';
const SMART_STANDBY_SECTION = 'smartStandbySection';
const POWER_SETTINGS_SECTION = 'powerSettingsSection';
const DPM_POWER_SETTINGS_SECTION = 'dpmPowerSettingsSection';
const ENERGY_STAR_SECTION = 'energyStarSection';

@Injectable({
	providedIn: 'root',
})
export class PowerPage extends PageVisibility {
	constructor(
		private powerContainer: PowerContainer,
		private powerSmartSettingsSection: PowerSmartSettingsSection,
		private batterySettingsSection: BatterySettingsSection,
		private smartStandbySection: SmartStandbySection,
		private powerSettingsSection: PowerSettingsSection,
		private EnergyStarSection: EnergyStarSection,
		private dpmPowerSettingsSection: DPMPowerSettingsSection,
		logger: LoggerService
	) {
		super(logger);
	}

	private isDPMSuported: boolean = false;

	protected async initialize(): Promise<void> {
		this.logger.info('PowerPage - initialize - Start');

		this.isDPMSuported = await this.powerContainer.isDPMToBePresented();

		this.logger.info('PowerPage - initialize - End', this.isDPMSuported);
	}

	protected getSectionVisibility(): Observable<{ value: boolean; type: string }> {
		const visibilityObservable = new Observable<{ value: boolean; type: string }>(
			(observer) => {
				const next = (value: boolean, type: string) => observer?.next({ value, type });

				if (this.isDPMSuported) {
					this.getDPMPowerSettingsSectionVisibility(next);
				} else {
					this.getNonDPMPowerSettingsSectionsVisibility(next);
				}
			}
		);

		return visibilityObservable;
	}

	private getNonDPMPowerSettingsSectionsVisibility(next: (value: boolean, type: string) => void) {
		this.getPowerSmartSettingsSectionVisibility(next);
		this.getBatterySettingsSectionVisibility(next);
		this.getSmartStandbySectionVisibility(next);
		this.getPowerSettingsSectionVisibility(next);
		this.getEnergyStarSectionSectionVisibility(next);
	}

	protected initializeLoadedResult() {
		let loadedResult = {};
		if (this.isDPMSuported) {
			loadedResult[DPM_POWER_SETTINGS_SECTION] = false;
		} else {
			loadedResult[POWER_SMART_SETTINGS_SECTION] = false;
			loadedResult[BATTERY_SETTINGS_SECTION] = false;
			loadedResult[SMART_STANDBY_SECTION] = false;
			loadedResult[POWER_SETTINGS_SECTION] = false;
			loadedResult[ENERGY_STAR_SECTION] = false;
		}

		return loadedResult;
	}

	private getPowerSmartSettingsSectionVisibility(next: (value: boolean, type: string) => void) {
		this.logger.info('PowerPage - getPowerSmartSettingsSectionVisibility - Before');
		this.powerSmartSettingsSection.isVisible().then((value) => {
			this.logger.info('PowerPage - getPowerSmartSettingsSectionVisibility:Then', value);
			const type = POWER_SMART_SETTINGS_SECTION;
			next(value, type);
		});
	}

	private getBatterySettingsSectionVisibility(next: (value: boolean, type: string) => void) {
		this.logger.info('PowerPage - getBatterySettingsSectionVisibility - Before');
		this.batterySettingsSection.isVisible().then((value) => {
			this.logger.info('PowerPage - getBatterySettingsSectionVisibility:Then', value);
			const type = BATTERY_SETTINGS_SECTION;
			next(value, type);
		});
	}

	private getSmartStandbySectionVisibility(next: (value: boolean, type: string) => void) {
		this.logger.info('PowerPage - getSmartStandbySectionVisibility - Before');
		this.smartStandbySection.isVisible().then((value) => {
			this.logger.info('PowerPage - getSmartStandbySectionVisibility - Then', value);
			const type = SMART_STANDBY_SECTION;
			next(value, type);
		});
	}

	private getPowerSettingsSectionVisibility(next: (value: boolean, type: string) => void) {
		this.logger.info('PowerPage - getPowerSettingsSectionVisibility - Before');
		this.powerSettingsSection.isVisible().then((value) => {
			this.logger.info('PowerPage - getPowerSettingsSectionVisibility - Then', value);
			const type = POWER_SETTINGS_SECTION;
			next(value, type);
		});
	}
	private getEnergyStarSectionSectionVisibility(next: (value: boolean, type: string) => void) {
		this.logger.info('EnergyStar - getEnergyStarSectionSectionVisibility - Before');
		this.EnergyStarSection.isVisible().then((value) => {
			this.logger.info('EnergyStar - getEnergyStarSectionSectionVisibility - Then', value);
			const type = ENERGY_STAR_SECTION;
			next(value, type);
		});
	}

	private getDPMPowerSettingsSectionVisibility(next: (value: boolean, type: string) => void) {
		this.logger.info('PowerPage - getDPMPowerSettingsSectionVisibility - Before');
		this.dpmPowerSettingsSection.isVisible().then((value) => {
			this.logger.info('PowerPage - getDPMPowerSettingsSectionVisibility - Then', value);
			const type = DPM_POWER_SETTINGS_SECTION;
			next(value, type);
		});
	}
}
